/**
 * Authentication state.
 *
 * The session snapshot returned by the API is the single source of truth for
 * who the user is, which school they are in, and what they may do. It is never
 * decoded from the JWT on the client — the token carries only role codes and a
 * permission version, and the server resolves the rest. A role change therefore
 * takes effect on the next request rather than at token expiry.
 * docs/04-auth-authz.md §4.1
 */

import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";

import { api, setAccessToken, setAuthLostHandler } from "@/shared/api/client";
import { AuthContext, type AuthContextValue, type AuthStatus } from "./context";
import type {
  AuthorizationResponse,
  CallbackResponse,
  LoginResult,
  SessionSnapshot,
  TenantSelectionRequired,
  TokenResponse,
} from "@/shared/api/types";
import type { OidcCompletion } from "./context";
import { isPlatformSession, needsTenantSelection } from "@/shared/api/types";

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<AuthStatus>("initialising");
  const [session, setSession] = useState<SessionSnapshot | null>(null);
  // Set when a federated sign-in lands on a user who belongs to several
  // schools; the login page picks it up and renders the chooser.
  const [pendingSelection, setPendingSelection] = useState<TenantSelectionRequired | null>(null);
  // Set when a federated sign-in turns out to be platform staff, who have no
  // school to be shown.
  const [platformSignIn, setPlatformSignIn] = useState(false);

  const clearPendingSelection = useCallback(() => setPendingSelection(null), []);
  const clearPlatformSignIn = useCallback(() => setPlatformSignIn(false), []);

  const applyTokens = useCallback((response: TokenResponse) => {
    setAccessToken(response.access_token);
    setSession(response.session);
    setStatus("authenticated");
  }, []);

  const clear = useCallback(() => {
    setAccessToken(null);
    setSession(null);
    setStatus("anonymous");
  }, []);

  // On a page load the access token is gone (it only ever lived in memory), but
  // the HttpOnly refresh cookie may still be valid. Silently re-establish.
  useEffect(() => {
    let cancelled = false;

    void (async () => {
      const recovered = await api.restoreSession();
      if (cancelled) return;
      if (!recovered) {
        clear();
        return;
      }
      try {
        const snapshot = await api.get<SessionSnapshot>("/auth/session");
        if (cancelled) return;
        setSession(snapshot);
        setStatus("authenticated");
      } catch {
        if (!cancelled) clear();
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [clear]);

  // The client calls this when a refresh fails and the session is unrecoverable.
  useEffect(() => {
    setAuthLostHandler(clear);
    return () => setAuthLostHandler(null);
  }, [clear]);

  const login = useCallback(
    async (identifier: string, password: string): Promise<LoginResult> => {
      const result = await api.post<LoginResult>(
        "/auth/login",
        { identifier, password },
        { skipAuthRetry: true },
      );
      // Two results are not a school session and must not be applied as one.
      //
      // A user with memberships at several schools must choose one first — an
      // access token is always bound to exactly one tenant.
      //
      // A platform super admin has no tenant at all. Their token satisfies no
      // route in this application, and the response carries no `session`, so
      // storing it would leave the app "authenticated" with nothing to show
      // and every subsequent request refused.
      if (!needsTenantSelection(result) && !isPlatformSession(result)) {
        applyTokens(result);
      }
      return result;
    },
    [applyTokens],
  );

  const selectTenant = useCallback(
    async (loginToken: string, membershipId: string): Promise<TokenResponse> => {
      const response = await api.post<TokenResponse>(
        "/auth/select-tenant",
        { login_token: loginToken, membership_id: membershipId },
        { skipAuthRetry: true },
      );
      applyTokens(response);
      return response;
    },
    [applyTokens],
  );

  const beginOidcLogin = useCallback(async (providerSlug: string, schoolSlug?: string) => {
    // The backend creates the authorization request — it holds the PKCE
    // verifier and the nonce server-side, so neither is ever exposed to a
    // script running in this page.
    const query = new URLSearchParams();
    if (schoolSlug) query.set("school", schoolSlug);
    query.set("return_to", window.location.pathname);

    const response = await api.post<AuthorizationResponse>(
      `/auth/oidc/${encodeURIComponent(providerSlug)}/authorize?${query}`,
      undefined,
      { skipAuthRetry: true },
    );
    // Full navigation, not a fetch: the user has to authenticate at the
    // provider's own origin, under their own session and MFA.
    window.location.assign(response.authorization_url);
  }, []);

  const completeOidcLogin = useCallback(
    async (code: string, state: string): Promise<OidcCompletion> => {
      const response = await api.post<CallbackResponse>(
        "/auth/oidc/callback",
        { code, state },
        { skipAuthRetry: true },
      );

      if (needsTenantSelection(response.result)) {
        // Federated users can belong to several schools too — same
        // tenant-selection step as a password login.
        setPendingSelection(response.result);
        setStatus("anonymous");
        return { portal: "admin", returnTo: "/login" };
      }

      if (isPlatformSession(response.result)) {
        // Authenticated, but into the wrong application. Their token satisfies
        // no route here, so send them back to the sign-in screen with an
        // explanation rather than storing a session that cannot load a page.
        setPlatformSignIn(true);
        setStatus("anonymous");
        return { portal: "admin", returnTo: "/login" };
      }

      applyTokens(response.result);
      return {
        portal: response.result.session.portal,
        returnTo: response.return_to,
      };
    },
    [applyTokens],
  );

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } finally {
      clear();
    }
  }, [clear]);

  const refreshSession = useCallback(async () => {
    const snapshot = await api.get<SessionSnapshot>("/auth/session");
    setSession(snapshot);
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      status,
      session,
      login,
      selectTenant,
      beginOidcLogin,
      completeOidcLogin,
      logout,
      refreshSession,
      pendingSelection,
      clearPendingSelection,
      platformSignIn,
      clearPlatformSignIn,
    }),
    [
      status,
      session,
      login,
      selectTenant,
      beginOidcLogin,
      completeOidcLogin,
      logout,
      refreshSession,
      pendingSelection,
      clearPendingSelection,
      platformSignIn,
      clearPlatformSignIn,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export type { TenantSelectionRequired };
